import {
  AMOUNT_INPUT,
  DonationAmount,
  DonationFrequency,
  FREQUENCY,
  useCustomEventListener,
} from '@classy/campaign-page-blocks'
import { EVENT } from 'models/event'
import { IntelligentAskAmountEventPayload } from 'hooks/useIntelligentAskAmount'
import { produce } from 'immer'

/**
 * Updates the passed amounts to their Intelligent Ask Amount (IAA) overrides.
 *
 * - Updates Donation block's donationAmount, recurringAmounts, and donationFrequencies props.
 * - Uses immer to create new object references. React hooks only trigger when references change.
 */
export const useIntelligentAskOverrides = (
  initialDonationAmounts: DonationAmount[],
  initialRecurringAmounts: DonationAmount[],
  initialDonationFrequencies: DonationFrequency[],
) => {
  const intelligentAskAmountData: IntelligentAskAmountEventPayload = useCustomEventListener(
    EVENT.IAA_FETCH_AMOUNTS,
    {
      isLoading: true,
      onetimeEnabled: false,
      recurringEnabled: false,
      grid: {
        suggested_donation_amount: null,
        suggested_recurring_amount: null,
      },
      is_default_recurring: null,
      show_recurring_upsell: null,
      suggested_donation_amount: null,
      suggested_recurring_amount: null,
    },
  )

  const { isLoading, onetimeEnabled, recurringEnabled } = intelligentAskAmountData
  const intelligentAskEnabled = onetimeEnabled || recurringEnabled

  if (!(onetimeEnabled || recurringEnabled) || isLoading) {
    return {
      donationAmounts: initialDonationAmounts,
      recurringAmounts: initialRecurringAmounts,
      donationFrequencies: initialDonationFrequencies,
      isLoading,
      intelligentAskEnabled,
      intelligentAskAmountData,
    }
  }

  const {
    grid: {
      suggested_donation_amount: suggestedDonationPresetAmounts,
      suggested_recurring_amount: suggestedRecurringPresetAmounts,
    },
    is_default_recurring: isDefaultFrequencyMonthly,
    show_recurring_upsell: forceRecurringNudgeTag,
    suggested_donation_amount: suggestedDonationOtherAmount,
    suggested_recurring_amount: suggestedRecurringOtherAmount,
  } = intelligentAskAmountData

  const newDonationAmounts = produce(initialDonationAmounts, (draft) => {
    if (!onetimeEnabled) return

    // For IAA, no items should be defaulted
    draft.forEach((amount) => {
      amount.isDefault = false
    })

    // Update preset amounts from grid
    if (suggestedDonationPresetAmounts) {
      draft.forEach((amount, index) => {
        if (amount.name.startsWith('preset') && suggestedDonationPresetAmounts[index]) {
          amount.amount = suggestedDonationPresetAmounts[index]
          amount.isVisible = true
        }
      })
    }

    // Set suggested amount in "other" field
    if (suggestedDonationOtherAmount) {
      const otherAmount = draft.find(({ name }) => name === AMOUNT_INPUT.OTHER)
      if (otherAmount) {
        otherAmount.amount = Number(suggestedDonationOtherAmount)
        otherAmount.isVisible = true
      }
    }
  })

  // TODO: CL-48068, move to newDonationAmounts when onetimeEnabled/recurringEnabled are combined
  const newRecurringAmounts = produce(initialRecurringAmounts, (draft) => {
    if (!recurringEnabled) return

    // For IAA, no items should be defaulted
    draft.forEach((amount) => {
      amount.isDefault = false
    })

    // Update preset amounts from grid
    if (suggestedRecurringPresetAmounts) {
      draft.forEach((amount, index) => {
        if (amount.name.startsWith('preset') && suggestedRecurringPresetAmounts[index]) {
          amount.amount = suggestedRecurringPresetAmounts[index]
          amount.isVisible = true
        }
      })
    }

    // Set suggested amount in "other" field
    if (suggestedRecurringOtherAmount) {
      const otherAmount = draft.find(({ name }) => name === AMOUNT_INPUT.OTHER)
      if (otherAmount) {
        otherAmount.amount = Number(suggestedRecurringOtherAmount)
        otherAmount.isVisible = true
      }
    }
  })

  /**
   * If IAA is enabled for both one time and recurring, the campaign is eligible for the default
   * recurring experiment for overriding the default frequency. If isDefaultFrequencyMonthly
   * (`is_default_recurring`) is true, set the default frequency to monthly. If false, set the
   * default frequency to one time. If null or undefined, no override is applied.
   *
   * TODO: CL-48068, merge onetimeEnabled and recurringEnabled into isEnabled
   */
  const newDonationFrequencies = produce(initialDonationFrequencies, (draft) => {
    // only eligible if both one time AND recurring are enabled
    if (!recurringEnabled && !onetimeEnabled) return

    // Ensure both one time and monthly frequencies are visible when IAA is enabled
    draft.forEach((frequency) => {
      if (frequency.name === FREQUENCY.ONE_TIME || frequency.name === FREQUENCY.MONTHLY) {
        frequency.isVisible = true
      }
    })

    // If isDefaultFrequencyMonthly is null or missing from the payload, the campaign is not
    // eligible for the experiment. Do not apply a frequency override and use block props instead.
    if (isDefaultFrequencyMonthly === null || isDefaultFrequencyMonthly === undefined) return

    if (isDefaultFrequencyMonthly) {
      draft.forEach((frequency) => {
        frequency.isDefault = frequency.name === FREQUENCY.MONTHLY
      })
    } else {
      draft.forEach((frequency) => {
        frequency.isDefault = frequency.name === FREQUENCY.ONE_TIME
      })
    }
  })

  return {
    donationAmounts: newDonationAmounts,
    recurringAmounts: newRecurringAmounts,
    donationFrequencies: newDonationFrequencies,
    showRecurringNudgeTag: forceRecurringNudgeTag ? true : undefined, // omit if not true
    isLoading,
    intelligentAskEnabled,
    intelligentAskAmountData,
  }
}
